import ThankYouModal from "./ThankYouModal";
import { useRef, useState } from "react";
import { submitLead } from "../lib/formSubmit";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2, ShieldCheck, Upload, X } from "lucide-react";
import { STATE_OPTIONS, SPEND_OPTIONS, SERVICE_STATES, getUtm } from "../data/siteData";
import { useLead } from "../lib/leadContext";
import { track } from "../lib/analytics";

const COMMODITIES = [
  { v: "electricity", l: "Electricity" },
  { v: "natural-gas", l: "Natural Gas" },
  { v: "both", l: "Both" },
];

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-[#0B1F44] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0066FF] focus:border-[#0066FF] transition";

export const BillUploadField = ({ file, setFile, testid }) => {
  const ref = useRef(null);
  return (
    <div>
      <span className="block text-sm font-semibold text-[#0B1F44]">
        Upload a recent utility bill <span className="font-normal text-slate-400">(PDF, JPG, or PNG, max 20 MB)</span>
      </span>
      {file ? (
        <div className="mt-2 flex items-center justify-between rounded-xl border border-[#0066FF]/40 bg-[#E6EEF7] px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-medium text-[#0B1F44] truncate">
            <Upload size={16} className="text-[#0066FF] shrink-0" />
            {file.name}
            <span className="text-xs text-slate-500">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
          </span>
          <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-rose-500" aria-label="Remove file">
            <X size={18} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          data-testid={testid}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0066FF] hover:bg-[#E6EEF7]/40 px-4 py-5 text-sm font-medium text-slate-500 hover:text-[#0066FF] transition-all"
        >
          <Upload size={18} />
          Choose a file
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 20 * 1024 * 1024) {
            toast.error("File must be under 20 MB.");
            return;
          }
          setFile(f);
        }}
      />
    </div>
  );
};

export const HoneypotField = () => (
  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
    className="absolute left-[-9999px] h-0 w-0 opacity-0" defaultValue="" />
);

export default function LeadForm() {
  const { lead, update, billFile, setBillFile } = useLead();
  const [local, setLocal] = useState({ state: "", monthly_spend: "", commodity: "electricity" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const loadedAt = useRef(Date.now());

  const setField = (k) => (e) => setLocal((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please consent to being contacted about your inquiry."); return; }
    if (!local.monthly_spend) {
      toast.error("Please select your monthly energy expense range.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("source", "homepage");
      fd.append("consent", String(consent));
      fd.append("business_name", lead.businessName || "");
      fd.append("contact_name", lead.contactName || "");
      fd.append("email", lead.email || "");
      fd.append("_replyto", lead.email || "");
      fd.append("phone", lead.phone || "");
      fd.append("message", lead.message || "");
      fd.append("state", local.state);
      fd.append("monthly_spend", local.monthly_spend);
      fd.append("commodity", local.commodity);
      fd.append("supply_rate_cents", lead.supplyRate || "");
      fd.append("monthly_kwh", lead.monthlyKwh || "");
      fd.append("calc_scenarios", JSON.stringify(lead.calcResults || []));
      Object.entries(getUtm()).forEach(([k, v]) => fd.append(k, v));
      fd.append("form_elapsed", String((Date.now() - loadedAt.current) / 1000));
      fd.append("website", e.target.website?.value || "");
      if (billFile) fd.append("bill", billFile);
      await submitLead(fd);
      track("lead_submitted", { source: "homepage", state: local.state });
      setDone(true);
      toast.success("Thank you! Your request has been submitted.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="get-quote" className="py-16 md:py-24 bg-[#0B1F44] relative overflow-hidden">
      <ThankYouModal open={done} onClose={() => setDone(false)} />
      <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-[#0066FF]/15 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="text-s font-bold uppercase tracking-[0.2em] text-[#4D94FF]">Review My Rates</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Get Your Free Bill Analysis
          </h2>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            Let our experts review your utility bill for free, and figure out the best rates for your business. Completely free, with no obligation to switch.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              "Response after reviewing your request",
              "Quotes from multiple licensed suppliers",
              "Zero disruption to your current service",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
                <CheckCircle2 size={18} className="text-[#4D94FF] shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            <ShieldCheck size={20} className="text-[#4D94FF] shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400 leading-relaxed">
              BlueSettle is a commercial energy procurement intermediary, not a utility. Your information and
              uploaded documents are stored privately and never sold.
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl bg-white p-6 sm:p-9 shadow-2xl">
            {done ? (
              <div className="py-14 text-center" data-testid="lead-form-success-message">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E6EEF7]">
                  <CheckCircle2 size={34} className="text-[#0066FF]" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-[#0B1F44]">Request received</h3>
                <p className="mt-3 text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
                  Thanks, {(lead.contactName || "").split(" ")[0] || "there"}. An energy specialist will review
                  your {SERVICE_STATES[Object.keys(SERVICE_STATES).find((k) => SERVICE_STATES[k].abbr === local.state)]?.name || "state"}{" "}
                  market options and reach out after reviewing your request.
                </p>
                <button
                  onClick={() => { setDone(false); setLocal({ state: "", monthly_spend: "", commodity: "electricity" }); }}
                  className="mt-8 text-sm font-semibold text-[#0066FF] hover:underline"
                  data-testid="lead-form-submit-another"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
                <HoneypotField />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input required placeholder="Business name *" className={inputCls} value={lead.businessName || ""} onChange={(e) => update({ businessName: e.target.value })} data-testid="lead-form-business-name" />
                  <input required placeholder="Contact name *" className={inputCls} value={lead.contactName || ""} onChange={(e) => update({ contactName: e.target.value })} data-testid="lead-form-contact-name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input required type="email" placeholder="Work email *" className={inputCls} value={lead.email || ""} onChange={(e) => update({ email: e.target.value })} data-testid="lead-form-email" />
                  <input type="tel" placeholder="Phone (optional)" className={inputCls} value={lead.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="lead-form-phone" />
                </div>

                <div>
                  <span className="block text-sm font-semibold text-[#0B1F44]">State *</span>
                  <select required className={`${inputCls} mt-2 ${local.state ? "" : "text-slate-400"}`} value={local.state} onChange={setField("state")} data-testid="lead-form-state-select">
                    <option value="" disabled>Select your state</option>
                    {STATE_OPTIONS.map((s) => (
                      <option key={s.abbr} value={s.abbr}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="block text-sm font-semibold text-[#0B1F44]">Monthly energy expense *</span>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {SPEND_OPTIONS.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setLocal((f) => ({ ...f, monthly_spend: o.id }))}
                        data-testid={`lead-form-spend-option-${o.id}`}
                        className={`rounded-xl border-2 px-3 py-3 text-xs sm:text-sm font-semibold transition-all ${
                          local.monthly_spend === o.id
                            ? "border-[#0066FF] bg-[#E6EEF7] text-[#0066FF]"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="block text-sm font-semibold text-[#0B1F44]">What do you want to optimize?</span>
                  <div className="mt-2 grid grid-cols-3 gap-3" data-testid="lead-form-commodity-toggle">
                    {COMMODITIES.map((c) => (
                      <button
                        key={c.v}
                        type="button"
                        onClick={() => setLocal((f) => ({ ...f, commodity: c.v }))}
                        className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-all ${
                          local.commodity === c.v
                            ? "border-[#0066FF] bg-[#E6EEF7] text-[#0066FF]"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                      >
                        {c.l}
                      </button>
                    ))}
                  </div>
                </div>

                <BillUploadField file={billFile} setFile={setBillFile} testid="lead-form-bill-upload" />

                <textarea
                  rows={3}
                  placeholder="Anything we should know? (contract end date, multiple locations, etc.)"
                  className={`${inputCls} resize-none`}
                  value={lead.message || ""}
                  onChange={(e) => update({ message: e.target.value })}
                  data-testid="lead-form-message"
                />

                <label className="flex items-start gap-3 text-xs text-slate-600"><input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#0066FF]"/> I consent to BlueSettle contacting me about this rate review. See our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>. This request does not authorize an energy supplier switch.</label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-form-submit-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-bold px-8 py-4 text-base transition-all shadow-[0_8px_30px_rgba(0,102,255,0.35)]"
                >
                  {submitting ? <Loader2 size={19} className="animate-spin" /> : <Send size={18} />}
                  {submitting ? "Sending..." : "Get My Free Rate Analysis"}
                </button>
                <p className="text-center text-xs text-slate-400">
                  Free and no-obligation. See our Privacy Policy for details on how information is handled.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
